import { map, switchMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin } from 'rxjs';
import { first } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { Note } from '../_models/note';
import { environment } from '../../../environments/environment';
import { AppState } from '../_reducers';
import { selectAllNotes } from '../_selectors/note.selectors';
import { User } from '../_models';
import { UserService } from './user.service';
import { FarmService } from './farm.service';

@Injectable()
export class NoteService {
  private apiBaseUrl = environment.apiBaseUrl;
  private noteList$: Observable<Note[]>;

  constructor(
    private store: Store<AppState>,
    private userService: UserService,
    private farmService: FarmService,
    private http: HttpClient
  ) {
    this.noteList$ = this.store.pipe(select(selectAllNotes));
  }

  getFromLocalStore(): Observable<Note[]> {
    return this.noteList$;
  }

  getNotes(): Observable<Note[]> {
    return this.http.get<Note[]>(`${this.apiBaseUrl}api/private/v1/notes/`).pipe(
      switchMap((notes: Note[]) => {
        const noteObservables: Observable<Note>[] = notes.map((note) =>
          this.updateNoteWithDetails(note)
        );
        return forkJoin(noteObservables) as Observable<Note[]>;
      })
    );
  }

  getNoteById(id: number): Observable<Note> {
    return this.noteList$.pipe(
      first((list: Note[]) => list && list.length > 0),
      map((list: Note[]) => list.find((note) => note.id === id))
    );
  }

  getNote(id: number): Observable<Note> {
    return this.http
      .get<Note>(this.apiBaseUrl + 'api/private/v1/notes/' + id)
      .pipe(map((response) => response));
  }

  saveNote(note: Note): Observable<Note> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    // Clona l'oggetto note
    const clonedNote: Note = { ...note };
    clonedNote.companyId = companySelected ? companySelected.id : null;
    delete clonedNote.lastUpdated;
    delete clonedNote.dateCreated;

    if (clonedNote.user != null && clonedNote.user.id != null) {
      clonedNote.user = { id: clonedNote.user.id } as User;
    }

    const saveObservable = clonedNote.id
      ? this.http.put<Note>(this.apiBaseUrl + 'api/private/v1/notes/' + clonedNote.id, clonedNote)
      : this.http.post<Note>(this.apiBaseUrl + 'api/private/v1/notes/', clonedNote);

    return saveObservable.pipe(switchMap((savedNote) => this.updateNoteWithDetails(savedNote)));
  }

  deleteNote(id: number): Observable<Note> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/notes/' + id).pipe(
      map((response) => {
        return { id } as Note;
      })
    );
  }

  getAllNoteTypes = () => {
    return [
      'general_note',
      'disease',
      'parasite',
      'water',
      'pest',
      'damage',
      'sampling',
      'cropAnalysis',
    ];
  };

  getNotesOPModule(farmsAppend): Observable<Note[]> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/notes' + farmsAppend)
      .pipe(map((response) => response));
  }

  getDamageLegendList = () => {
    return [
      {
        id: 0,
        status: 'null',
        color: '#BBBBBB',
      },
      {
        id: 1,
        status: 'low',
        color: '#00B050',
      },
      {
        id: 2,
        status: 'medium_low',
        color: '#92D050',
      },
      {
        id: 3,
        status: 'medium',
        color: '#FFE699',
      },
      {
        id: 4,
        status: 'medium_high',
        color: '#FFC000',
      },
      {
        id: 5,
        status: 'high',
        color: '#FF0000',
      },
    ];
  };

  getNoteTypeColor(code: string) {
    switch (code) {
      case 'general_note':
        return '#FFFFFF';
      case 'disease':
        return '#E55858';
      case 'parasite':
        return '#E5B358';
      case 'water':
        return '#58C0E5';
      case 'pest':
        return '#AFE558';
      case 'damage':
        return '#CF58E5';
      case 'sampling':
        return '#5C58E5';
      case 'cropAnalysis':
        return '#509c5e';
      default:
        return '#FFFFFF';
    }
  }

  private updateNoteWithDetails(note: Note): Observable<Note> {
    return this.userService.getUserById(note.user.id).pipe(
      map((user) => {
        const updatedNote = { ...note, user };
        updatedNote.companyName = this.farmService.getFarmNameByID(updatedNote.companyId);
        return updatedNote;
      })
    );
  }
}
